import React, { useState, useEffect } from 'react';
import { 
  FaShieldAlt, 
  FaSave, 
  FaUndo, 
  FaCheck, 
  FaTimes,
  FaSpinner,
  FaExclamationTriangle,
  FaCheckCircle,
  FaInfoCircle,
  FaLock,
  FaUserShield,
  FaEdit,
  FaRedo
} from 'react-icons/fa';
import { useAdminAuth } from '../context/AdminAuthContext';
import { API_ENDPOINTS } from '../../config/api';

const RolePermissionsManagement = () => {
  const { token } = useAdminAuth();
  const [rolePermissions, setRolePermissions] = useState({});
  const [allRoles, setAllRoles] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [hasChanges, setHasChanges] = useState({});
  const [originalPermissions, setOriginalPermissions] = useState({});

  // Group permissions by module
  const groupedPermissions = allPermissions.reduce((acc, perm) => {
    const [module] = perm.split(':');
    if (!acc[module]) acc[module] = [];
    acc[module].push(perm);
    return acc;
  }, {});

  // Role display names
  const roleDisplayNames = {
    super_admin: 'Super Admin',
    admin: 'Admin',
    support: 'Support',
    viewer: 'Viewer'
  };

  // Permission display names
  const permissionDisplayNames = {
    'customers:read': 'View Customers',
    'customers:write': 'Manage Customers',
    'billing:read': 'View Billing',
    'billing:write': 'Manage Billing',
    'plans:read': 'View Plans',
    'plans:write': 'Manage Plans',
    'tickets:read': 'View Tickets',
    'tickets:write': 'Manage Tickets',
    'admin_users:read': 'View Admin Users',
    'admin_users:write': 'Manage Admin Users',
    'system:settings': 'System Settings'
  };

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    loadRolePermissions();
  }, [token]);

  const loadRolePermissions = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(API_ENDPOINTS.ROLE_PERMISSIONS_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ action: 'get-all' })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load role permissions');
      }

      if (data.success) {
        setRolePermissions(data.rolePermissions || {});
        setAllRoles(data.allRoles || []);
        setAllPermissions(data.allPermissions || []);
        setOriginalPermissions({ ...data.rolePermissions });
      }
    } catch (err) {
      console.error('Error loading role permissions:', err);
      setError(err.message || 'Failed to load role permissions');
    } finally {
      setLoading(false);
    }
  };

  const togglePermission = (role, permission) => {
    const currentPermissions = rolePermissions[role] || [];
    const newPermissions = currentPermissions.includes(permission)
      ? currentPermissions.filter(p => p !== permission)
      : [...currentPermissions, permission];
    
    setRolePermissions(prev => ({
      ...prev,
      [role]: newPermissions.sort()
    }));

    // Track changes
    const original = originalPermissions[role] || [];
    const hasChanged = JSON.stringify(newPermissions.sort()) !== JSON.stringify(original.sort());
    setHasChanges(prev => ({
      ...prev,
      [role]: hasChanged
    }));
  };

  const saveRolePermissions = async (role) => {
    setSaving(prev => ({ ...prev, [role]: true }));
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(API_ENDPOINTS.ROLE_PERMISSIONS_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'update-role',
          role: role,
          permissions: rolePermissions[role] || []
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to update role permissions');
      }

      if (data.success) {
        setSuccess(`Permissions updated successfully for ${roleDisplayNames[role]}`);
        setOriginalPermissions(prev => ({
          ...prev,
          [role]: [...(rolePermissions[role] || [])]
        }));
        setHasChanges(prev => ({
          ...prev,
          [role]: false
        }));
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error saving role permissions:', err);
      setError(err.message || 'Failed to save role permissions');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(prev => ({ ...prev, [role]: false }));
    }
  };

  const resetRolePermissions = async (role) => {
    if (!window.confirm(`Reset ${roleDisplayNames[role]} permissions to defaults?`)) {
      return;
    }

    setSaving(prev => ({ ...prev, [`reset_${role}`]: true }));
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch(API_ENDPOINTS.ROLE_PERMISSIONS_MANAGEMENT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          action: 'reset-role',
          role: role
        })
      });

      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset role permissions');
      }

      if (data.success) {
        setRolePermissions(prev => ({
          ...prev,
          [role]: data.permissions || []
        }));
        setOriginalPermissions(prev => ({
          ...prev,
          [role]: [...(data.permissions || [])]
        }));
        setHasChanges(prev => ({
          ...prev,
          [role]: false
        }));
        setSuccess(`Permissions reset to defaults for ${roleDisplayNames[role]}`);
        setTimeout(() => setSuccess(null), 3000);
      }
    } catch (err) {
      console.error('Error resetting role permissions:', err);
      setError(err.message || 'Failed to reset role permissions');
      setTimeout(() => setError(null), 5000);
    } finally {
      setSaving(prev => ({ ...prev, [`reset_${role}`]: false }));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <FaSpinner className="animate-spin text-4xl text-[#fabd2f] mx-auto mb-4" />
          <p className="text-[#ebdbb2]">Loading role permissions...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-[#ebdbb2] mb-2 flex items-center gap-3">
              <FaShieldAlt className="text-[#fabd2f]" />
              Role & Permission Management
            </h1>
            <p className="text-[#bdae93]">Configure permissions for each admin role</p>
          </div>
          <button
            onClick={loadRolePermissions}
            className="px-4 py-2 bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] rounded-lg border border-[#504945] transition-colors flex items-center gap-2"
          >
            <FaRedo /> Refresh
          </button>
        </div>
      </div>

      {/* Messages */}
      {error && (
        <div className="mb-4 p-4 bg-[#3c3836] border border-[#fb4934] rounded-lg flex items-center gap-3">
          <FaExclamationTriangle className="text-[#fb4934] flex-shrink-0" />
          <p className="text-[#fb8071]">{error}</p>
        </div>
      )}

      {success && (
        <div className="mb-4 p-4 bg-[#3c3836] border border-[#b8bb26] rounded-lg flex items-center gap-3">
          <FaCheckCircle className="text-[#b8bb26] flex-shrink-0" />
          <p className="text-[#d5e39f]">{success}</p>
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-6 p-4 bg-[#282828] border border-[#504945] rounded-lg flex items-start gap-3">
        <FaInfoCircle className="text-[#fabd2f] mt-0.5 flex-shrink-0" />
        <div className="text-[#bdae93] text-sm">
          <p className="font-semibold text-[#ebdbb2] mb-1">Permission Management</p>
          <p>Toggle permissions for each role. Changes are saved per role. Only Super Admin can modify permissions.</p>
        </div>
      </div>

      {/* Role-Permission Matrix Table */}
      <div className="bg-[#1d2021] border border-[#3c3836] rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[#282828] border-b border-[#3c3836]">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-[#ebdbb2] sticky left-0 bg-[#282828] z-10">
                  Permission
                </th>
                {allRoles.map(role => (
                  <th key={role} className="px-6 py-4 text-center text-sm font-semibold text-[#ebdbb2] min-w-[200px]">
                    <div className="flex flex-col items-center gap-2">
                      <div className="flex items-center gap-2">
                        {role === 'super_admin' && <FaLock className="text-[#fabd2f]" />}
                        <span>{roleDisplayNames[role]}</span>
                      </div>
                      {hasChanges[role] && (
                        <span className="text-xs text-[#fabd2f]">* Unsaved</span>
                      )}
                      <div className="flex gap-2 mt-1">
                        <button
                          onClick={() => saveRolePermissions(role)}
                          disabled={!hasChanges[role] || saving[role]}
                          className="px-3 py-1 text-xs bg-[#fabd2f] hover:bg-[#fe8019] text-[#1d2021] rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1"
                        >
                          {saving[role] ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <FaSave />
                          )}
                          Save
                        </button>
                        <button
                          onClick={() => resetRolePermissions(role)}
                          disabled={saving[`reset_${role}`]}
                          className="px-3 py-1 text-xs bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] rounded disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-1 border border-[#504945]"
                          title="Reset to defaults"
                        >
                          {saving[`reset_${role}`] ? (
                            <FaSpinner className="animate-spin" />
                          ) : (
                            <FaUndo />
                          )}
                        </button>
                      </div>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {Object.entries(groupedPermissions).map(([module, perms]) => (
                <React.Fragment key={module}>
                  <tr className="bg-[#282828] border-b border-[#3c3836]">
                    <td colSpan={allRoles.length + 1} className="px-6 py-2">
                      <span className="text-sm font-semibold text-[#fabd2f] uppercase">
                        {module}
                      </span>
                    </td>
                  </tr>
                  {perms.map(permission => (
                    <tr 
                      key={permission} 
                      className="border-b border-[#3c3836] hover:bg-[#282828] transition-colors"
                    >
                      <td className="px-6 py-3 text-sm text-[#ebdbb2] sticky left-0 bg-[#1d2021] z-10">
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-xs text-[#7c6f64]">{permission}</span>
                          <span className="text-[#bdae93]">•</span>
                          <span>{permissionDisplayNames[permission] || permission}</span>
                        </div>
                      </td>
                      {allRoles.map(role => {
                        const isChecked = (rolePermissions[role] || []).includes(permission);
                        const isSuperAdmin = role === 'super_admin';
                        
                        return (
                          <td key={role} className="px-6 py-3 text-center">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={() => togglePermission(role, permission)}
                                disabled={saving[role] || saving[`reset_${role}`]}
                                className="w-5 h-5 rounded border-[#504945] bg-[#3c3836] text-[#fabd2f] focus:ring-2 focus:ring-[#fabd2f] focus:ring-offset-2 focus:ring-offset-[#1d2021] disabled:opacity-50 disabled:cursor-not-allowed"
                              />
                              {isChecked && (
                                <FaCheck className="ml-2 text-[#b8bb26]" />
                              )}
                            </label>
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Footer Info */}
      <div className="mt-6 p-4 bg-[#282828] border border-[#504945] rounded-lg">
        <div className="flex items-start gap-3">
          <FaUserShield className="text-[#fabd2f] mt-0.5 flex-shrink-0" />
          <div className="text-[#bdae93] text-sm">
            <p className="font-semibold text-[#ebdbb2] mb-2">Role Hierarchy</p>
            <ul className="space-y-1 list-disc list-inside">
              <li><strong className="text-[#ebdbb2]">Super Admin:</strong> Full system access, can manage all admin users</li>
              <li><strong className="text-[#ebdbb2]">Admin:</strong> Can manage customers, billing, plans, and tickets</li>
              <li><strong className="text-[#ebdbb2]">Support:</strong> Read-only access + can manage tickets</li>
              <li><strong className="text-[#ebdbb2]">Viewer:</strong> Read-only access to all modules</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RolePermissionsManagement;

